/**
 * A manual journal's lines as the grid edits them, and as the server takes
 * them. Pure, so the request rules are testable: every empty cell is left out
 * (never ""), a line carries a debit or a credit only when one was typed, a
 * blank row is not sent, and no two lines share a line number.
 */
import { subMinor, sumMinor } from "@linkhr/accounting-domain";
import type { components } from "@linkhr/api-client/core";
import type { Account } from "../api/ledger";

type S = components["schemas"];
export type DraftRequest = S["AcctDraftRequest"];
export type DraftLineRequest = S["AcctDraftLineRequest"];
type DraftLine = S["AcctDraftLine"];
type Journal = S["AcctJournalView"];

/** The source a manual journal posts as: accounts and journals list the sources they take. */
export const MANUAL_JOURNAL = "MANUAL_JOURNAL";

/** One row as typed: ids or "", amounts as decimal strings at the currency's scale or "". */
export interface GridLine {
  lineNo: number;
  accountId: string;
  description: string;
  contactId: string;
  branchId: string;
  costCentreId: string;
  debit: string;
  credit: string;
}

export interface DraftHeader {
  journalId: string;
  /** `YYYY-MM-DD`. */
  accountingDate: string;
  reference: string;
  description: string;
}

export const blankLine = (lineNo: number): GridLine => ({
  lineNo,
  accountId: "",
  description: "",
  contactId: "",
  branchId: "",
  costCentreId: "",
  debit: "",
  credit: "",
});

/** Above every line's number, so an added line never takes one already used. */
export const nextLineNo = (lines: readonly GridLine[]): number =>
  lines.reduce((max, line) => Math.max(max, line.lineNo), 0) + 1;

/** A row with nothing in it: it is not sent, so it raises no problem. */
export const isBlank = (line: GridLine): boolean =>
  [
    line.accountId,
    line.description.trim(),
    line.contactId,
    line.branchId,
    line.costCentreId,
    line.debit,
    line.credit,
  ].every((value) => value === "");

/** A saved draft's lines as grid rows; a draft starts with two empty rows. */
export function gridLines(lines: readonly DraftLine[] | null | undefined): GridLine[] {
  const rows = (lines ?? []).map((line) => ({
    lineNo: line.lineNo,
    accountId: line.accountId ?? "",
    description: line.description ?? "",
    contactId: line.contactId ?? "",
    branchId: line.branchId ?? "",
    costCentreId: line.costCentreId ?? "",
    debit: line.debit ?? "",
    credit: line.credit ?? "",
  }));
  while (rows.length < 2) rows.push(blankLine(nextLineNo(rows)));
  return rows;
}

/** One row as the server takes it: only the cells that hold something. */
export function lineRequest(line: GridLine): DraftLineRequest {
  const description = line.description.trim();
  return {
    lineNo: line.lineNo,
    ...(line.accountId !== "" ? { accountId: line.accountId } : {}),
    ...(description !== "" ? { description } : {}),
    ...(line.contactId !== "" ? { contactId: line.contactId } : {}),
    ...(line.branchId !== "" ? { branchId: line.branchId } : {}),
    ...(line.costCentreId !== "" ? { costCentreId: line.costCentreId } : {}),
    ...(line.debit !== "" ? { debit: line.debit } : {}),
    ...(line.credit !== "" ? { credit: line.credit } : {}),
  };
}

/** The draft as saved: a manual journal of the non-blank rows. */
export function draftRequest(header: DraftHeader, lines: readonly GridLine[]): DraftRequest {
  const reference = header.reference.trim();
  const description = header.description.trim();
  return {
    kind: "MANUAL",
    journalId: header.journalId,
    accountingDate: header.accountingDate,
    ...(reference !== "" ? { reference } : {}),
    ...(description !== "" ? { description } : {}),
    lines: lines.filter((line) => !isBlank(line)).map(lineRequest),
  };
}

export interface GridTotals {
  debit: string;
  credit: string;
  /** Debits less credits. */
  difference: string;
}

/**
 * What the typed lines add up to, as a preview while they are typed (the
 * saved draft's totals come from the server). Null when an amount has more
 * decimals than the currency: the saved draft names that line.
 */
export function gridTotals(lines: readonly GridLine[], scale: number): GridTotals | null {
  try {
    const debit = sumMinor(
      lines.map((line) => line.debit).filter((amount) => amount !== ""),
      scale,
    );
    const credit = sumMinor(
      lines.map((line) => line.credit).filter((amount) => amount !== ""),
      scale,
    );
    return { debit, credit, difference: subMinor(debit, credit, scale) };
  } catch {
    return null;
  }
}

/** A source list of null takes every source; a list takes only those named. */
export const takesSource = (
  sources: readonly string[] | null | undefined,
  source: string,
): boolean => sources === null || sources === undefined || sources.includes(source);

// Answer records declare nothing required: an unset date leaves that side open.
const activeOn = (account: Account, onDate: string): boolean =>
  (account.activeFrom ?? "") <= onDate && (account.activeTo ?? "9999-12-31") >= onDate;

/**
 * The accounts a manual journal line can take on `onDate`: leaves, active
 * that day, and open to manual journals.
 */
export function postableAccounts(accounts: readonly Account[], onDate: string): Account[] {
  return accounts.filter(
    (account) =>
      !account.isGroup &&
      activeOn(account, onDate) &&
      takesSource(account.allowedSources, MANUAL_JOURNAL),
  );
}

/**
 * The accounts a supplier bill line (a credit's with `SUPPLIER_CREDIT`) can take on `onDate`:
 * leaves active that day, never a control account, open to the source, and not one only the
 * system posts to (`restricted`; absent until the chart's final export, so read as false).
 */
export const billAccounts = (
  accounts: readonly Account[],
  onDate: string,
  source = "SUPPLIER_BILL",
): Account[] =>
  accounts.filter(
    (account) =>
      !account.isGroup &&
      !account.isControl &&
      activeOn(account, onDate) &&
      takesSource(account.allowedSources, source) &&
      account.restricted !== true,
  );

/** The journals a manual journal can be kept in. */
export const manualJournals = (journals: readonly Journal[]): Journal[] =>
  journals.filter((journal) => takesSource(journal.allowedSources, MANUAL_JOURNAL));

export type Cell = "contact" | "branch" | "costCentre";

/**
 * The cells a line's account requires and the line leaves empty: a control
 * account needs a contact, and an account may require a branch or a cost
 * centre. A mark only; the server's answer stays the truth.
 */
export function missingCells(account: Account | undefined, line: GridLine): Cell[] {
  if (account === undefined) return [];
  const required = account.requiredDimensions ?? [];
  return [
    ...(account.isControl && line.contactId === "" ? (["contact"] as const) : []),
    ...(required.includes("BRANCH") && line.branchId === "" ? (["branch"] as const) : []),
    ...(required.includes("COST_CENTER") && line.costCentreId === ""
      ? (["costCentre"] as const)
      : []),
  ];
}
